/**
 * React Query read around
 * `GET /api/personas/:id/render-prompt?include_breakdown=true`.
 *
 * Keyed on `bundleId` so the inspector refreshes for free after Save —
 * saving mints a NEW bundle id (PUT + activate), so the key changes and
 * React Query refetches with no invalidation plumbing. Same mechanism
 * `useSimulatePreview` relies on.
 *
 * `enabled` is driven by the modal's open state: closed inspector = no
 * request. This endpoint makes no LLM call (unlike simulate), so opening
 * it is cheap.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type { ComposedPromptResponse } from '@/types/composedPrompt'

interface Args {
  personaId: string
  bundleId: string | null
  /**
   * Reconstruct runtime layers against this conversation's current state
   * (the "Rendered for" picker) instead of the saved-bundle-only render.
   * Omitted/null = saved bundle only, the original behavior.
   */
  conversationId?: string | null
  enabled: boolean
}

export function usePersonaComposedPrompt({
  personaId,
  bundleId,
  conversationId,
  enabled,
}: Args): UseQueryResult<ComposedPromptResponse, Error> {
  return useQuery<ComposedPromptResponse, Error>({
    queryKey: ['composed-prompt', personaId, bundleId, conversationId ?? null],
    queryFn: () =>
      apiClient.get<ComposedPromptResponse>(
        `/api/personas/${personaId}/render-prompt?include_breakdown=true${
          conversationId ? `&conversation_id=${conversationId}` : ''
        }`,
      ),
    enabled: enabled && bundleId !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })
}
